import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import Layout from "@/components/Layout";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { UserPlus, Award, MessageCircle, Users, TrendingUp, Printer } from "lucide-react";
import { toast } from "sonner";

function Stat({ icon: Icon, label, value, sub, tint }) {
  return (
    <Card className="p-5">
      <div className="flex items-center gap-2 label-caps mb-1">
        <Icon size={14} className={tint || "text-primary"} />
        {label}
      </div>
      <div className="font-serif text-5xl leading-none mt-1">{value}</div>
      {sub && <div className="text-xs text-muted-foreground mt-2">{sub}</div>}
    </Card>
  );
}

export default function Digest() {
  const [digest, setDigest] = useState(null);

  useEffect(() => {
    api.get("/admin/digest").then(r => setDigest(r.data)).catch(() => toast.error("Digest failed to load"));
  }, []);

  const printDigest = () => window.print();

  if (!digest) return <Layout><div className="p-10">Building digest…</div></Layout>;

  const { totals, new_students, exam_passes, open_questions, chapters_passed_last_7d, generated_at, period_start } = digest;

  return (
    <Layout>
      <div className="max-w-5xl mx-auto p-6 lg:p-12">
        <div className="flex items-baseline justify-between mb-8 flex-wrap gap-4 print:hidden">
          <div>
            <div className="label-caps mb-3">Instructor view</div>
            <h1 className="font-serif text-4xl lg:text-6xl leading-none">Weekly digest.</h1>
            <div className="text-sm text-muted-foreground mt-2">
              {new Date(period_start).toLocaleDateString()} → {new Date(generated_at).toLocaleDateString()}
            </div>
          </div>
          <Button onClick={printDigest} data-testid="print-digest-btn"><Printer size={14} className="mr-2" /> Print</Button>
        </div>

        <section className="grid md:grid-cols-4 gap-4 mb-10" data-testid="digest-totals">
          <Stat icon={Users} label="Enrolled" value={totals.students} sub={`${totals.active_students_last_7d} active this week`} />
          <Stat icon={UserPlus} label="New signups" value={new_students.length} sub="last 7 days" tint="text-accent" />
          <Stat icon={Award} label="Exam passes" value={exam_passes.length} sub={`${totals.exam_passed_all_time} all-time`} />
          <Stat icon={MessageCircle} label="Open questions" value={open_questions.length} sub={open_questions.length ? "Needs your reply" : "All caught up"} tint={open_questions.length ? "text-accent" : "text-primary"} />
        </section>

        <section className="mb-10">
          <h2 className="font-serif text-2xl mb-3 flex items-center gap-2"><TrendingUp size={18} /> Learning activity</h2>
          <Card className="p-5 text-sm">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <div className="label-caps">Chapters passed this week</div>
                <div className="font-serif text-4xl leading-none mt-1">{chapters_passed_last_7d}</div>
              </div>
              <div>
                <div className="label-caps">Weekly retention</div>
                <div className="font-serif text-4xl leading-none mt-1">{totals.students > 0 ? Math.round((totals.active_students_last_7d / totals.students) * 100) : 0}%</div>
                <div className="text-xs text-muted-foreground mt-1">{totals.active_students_last_7d} of {totals.students} students opened the app</div>
              </div>
            </div>
          </Card>
        </section>

        <section className="mb-10">
          <h2 className="font-serif text-2xl mb-3 flex items-center gap-2"><UserPlus size={18} className="text-accent" /> New students</h2>
          {new_students.length === 0 ? (
            <Card className="p-5 text-sm text-muted-foreground">No new signups this week.</Card>
          ) : (
            <div className="space-y-2" data-testid="digest-new-students">
              {new_students.map(s => (
                <Link key={s.id} to={`/admin/students/${s.id}`}>
                  <Card className="p-4 flex justify-between hover:border-primary transition-colors">
                    <div>
                      <div className="font-serif text-lg">{s.name}</div>
                      <div className="text-xs text-muted-foreground">{s.email}</div>
                    </div>
                    <div className="text-xs text-muted-foreground">{new Date(s.created_at).toLocaleDateString()}</div>
                  </Card>
                </Link>
              ))}
            </div>
          )}
        </section>

        <section className="mb-10">
          <h2 className="font-serif text-2xl mb-3 flex items-center gap-2"><Award size={18} className="text-primary" /> Exam passes</h2>
          {exam_passes.length === 0 ? (
            <Card className="p-5 text-sm text-muted-foreground">Nobody passed the final exam this week.</Card>
          ) : (
            <div className="space-y-2" data-testid="digest-exam-passes">
              {exam_passes.map(e => (
                <Card key={e.user_id} className="p-4 flex justify-between">
                  <div>
                    <div className="font-serif text-lg">{e.student_name || "Student"}</div>
                    <div className="text-xs text-muted-foreground">{e.student_email}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-mono text-sm">{e.score}%</div>
                    <div className="text-xs text-muted-foreground">{new Date(e.completed_at).toLocaleDateString()}</div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </section>

        <section>
          <h2 className="font-serif text-2xl mb-3 flex items-center gap-2"><MessageCircle size={18} className="text-accent" /> Unanswered questions</h2>
          {open_questions.length === 0 ? (
            <Card className="p-5 text-sm text-muted-foreground">All questions answered. Nice work.</Card>
          ) : (
            <div className="space-y-2" data-testid="digest-open-questions">
              {open_questions.map(q => (
                <Card key={q.id} className="p-4" data-testid={`digest-open-q-${q.id}`}>
                  <div className="flex items-center justify-between mb-1">
                    <div className="text-xs text-muted-foreground">from <strong className="text-foreground">{q.student_name}</strong> · {new Date(q.created_at).toLocaleDateString()}{q.chapter_id ? ` · Ch ${q.chapter_id}` : ""}</div>
                    <Link to="/questions" className="text-xs underline">Reply →</Link>
                  </div>
                  <div className="text-sm whitespace-pre-wrap">{q.text}</div>
                </Card>
              ))}
            </div>
          )}
        </section>
      </div>
    </Layout>
  );
}
